import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/* Scroll reveal wrapper */
export function Reveal({ children, delay = 0, y = 28, className = "", ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* Section heading block */
export function SectionHeading({ eyebrow, title, highlight, sub, align = "center", className = "" }) {
  const alignCls = align === "left" ? "items-start text-left" : "items-center text-center";
  return (
    <div className={`flex flex-col ${alignCls} gap-4 ${className}`}>
      {eyebrow && (
        <Reveal>
          <div className="flex items-center gap-3">
            <span className="h-px w-8 gold-line" />
            <span className="eyebrow">{eyebrow}</span>
            <span className="h-px w-8 gold-line" />
          </div>
        </Reveal>
      )}
      <Reveal delay={0.05}>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.08] max-w-3xl">
          {title} {highlight && <span className="gold-text">{highlight}</span>}
        </h2>
      </Reveal>
      {sub && (
        <Reveal delay={0.1}>
          <p className={`text-[15px] md:text-base text-[var(--muted)] max-w-2xl leading-relaxed ${align === "left" ? "" : "mx-auto"}`}>
            {sub}
          </p>
        </Reveal>
      )}
    </div>
  );
}

export function Pill({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border border-[var(--trt-line)] bg-[rgba(244,201,93,0.06)] px-3 py-1 text-xs font-mono text-[var(--gold-2)] ${className}`}>
      {children}
    </span>
  );
}

/* Count-up animated number */
export function useCountUp(target, { duration = 1800, decimals = 0 } = {}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return [ref, decimals ? val.toFixed(decimals) : Math.round(val)];
}

export function Counter({ to, decimals = 0, prefix = "", suffix = "", className = "" }) {
  const [ref, val] = useCountUp(to, { decimals });
  const formatted = decimals
    ? Number(val).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : Number(val).toLocaleString();
  return (
    <span ref={ref} className={`notranslate ${className}`} translate="no">
      {prefix}{formatted}{suffix}
    </span>
  );
}
